import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Shield, ChevronDown, ChevronRight, Check, Users, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const RolesPermissionsPage = () => {
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState({});
  const [selectedRole, setSelectedRole] = useState(null);
  const [editPerms, setEditPerms] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes] = await Promise.all([
        api.get('/roles'),
        api.get('/permissions'),
      ]);
      setRoles(rolesRes.data);
      setAllPermissions(permsRes.data);
    } catch (e) { toast.error('Failed to load'); }
    setLoading(false);
  };

  const selectRole = (role) => {
    setSelectedRole(role);
    setEditPerms([...(role.permissions || [])]);
    // Expand all groups by default
    const groups = {};
    Object.keys(allPermissions).forEach(g => groups[g] = true);
    setExpandedGroups(groups);
  };

  const togglePerm = (perm) => {
    setEditPerms(prev => prev.includes(perm) ? prev.filter(p => p !== perm) : [...prev, perm]);
  };

  const toggleGroup = (group) => {
    const groupPerms = Object.keys(allPermissions[group] || {});
    const allSelected = groupPerms.every(p => editPerms.includes(p));
    if (allSelected) {
      setEditPerms(prev => prev.filter(p => !groupPerms.includes(p)));
    } else {
      setEditPerms(prev => [...new Set([...prev, ...groupPerms])]);
    }
  };

  const savePermissions = async () => {
    if (!selectedRole) return;
    setSaving(true);
    try {
      await api.put(`/roles/${selectedRole.id}`, { permissions: editPerms });
      toast.success('Permissions saved');
      await load();
      // Re-select
      const updated = (await api.get('/roles')).data.find(r => r.id === selectedRole.id);
      if (updated) selectRole(updated);
    } catch (e) { toast.error('Failed to save'); }
    setSaving(false);
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Loading...</div>;

  return (
    <div className="flex gap-6 h-full" data-testid="roles-permissions-page">
      {/* Roles List */}
      <div className="w-72 flex-shrink-0">
        <h2 className="text-lg font-bold text-[#1f1f1f] mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#C5A059]" /> Roles
        </h2>
        <div className="space-y-1">
          {roles.map(role => (
            <button
              key={role.id}
              onClick={() => selectRole(role)}
              className={`w-full text-left px-3 py-2.5 border transition-colors ${
                selectedRole?.id === role.id ? 'border-[#C5A059] bg-[#FFFDF5]' : 'border-gray-200 bg-white hover:bg-gray-50'
              }`}
              data-testid={`role-${role.name}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-[#1f1f1f] capitalize">{role.name.replace(/_/g, ' ')}</span>
                {role.is_system && <span className="text-[9px] px-1.5 py-0.5 bg-gray-100 text-gray-500 uppercase tracking-wider">System</span>}
              </div>
              <p className="text-xs text-gray-400 mt-0.5">{role.description}</p>
              <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                <Users className="w-3 h-3" /> {role.user_count} users
                <span className="ml-auto">{(role.permissions || []).length} perms</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Permissions Editor */}
      <div className="flex-1">
        {!selectedRole ? (
          <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
            Select a role to view and edit permissions
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-[#1f1f1f] capitalize">{selectedRole.name.replace(/_/g, ' ')} Permissions</h2>
                <p className="text-xs text-gray-400">{editPerms.length} permissions selected</p>
              </div>
              <Button onClick={savePermissions} disabled={saving} className="bg-[#1f1f1f] hover:bg-[#333333] text-white gap-2" data-testid="save-permissions-btn">
                <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>

            <div className="space-y-2">
              {Object.entries(allPermissions).map(([group, perms]) => {
                const groupPerms = Object.keys(perms);
                const selectedCount = groupPerms.filter(p => editPerms.includes(p)).length;
                const allSelected = selectedCount === groupPerms.length;
                const expanded = expandedGroups[group];

                return (
                  <div key={group} className="border border-gray-200 bg-white" data-testid={`perm-group-${group}`}>
                    <button
                      onClick={() => setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }))}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                    >
                      {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                      <span className="text-sm font-semibold text-[#1f1f1f] capitalize">{group.replace(/_/g, ' ')}</span>
                      <span className="ml-auto text-xs text-gray-400">{selectedCount}/{groupPerms.length}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleGroup(group); }}
                        className={`px-2 py-0.5 text-[10px] uppercase tracking-wider font-medium border transition-colors ${
                          allSelected ? 'bg-[#C5A059] text-white border-[#C5A059]' : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'
                        }`}
                      >
                        {allSelected ? 'All' : 'Select All'}
                      </button>
                    </button>

                    {expanded && (
                      <div className="px-4 pb-3 grid grid-cols-2 gap-1.5">
                        {Object.entries(perms).map(([key, desc]) => (
                          <button
                            key={key}
                            onClick={() => togglePerm(key)}
                            className={`flex items-center gap-2 px-3 py-2 text-left border transition-colors ${
                              editPerms.includes(key) ? 'border-[#C5A059] bg-[#FFFDF5]' : 'border-gray-100 hover:bg-gray-50'
                            }`}
                            data-testid={`perm-${key}`}
                          >
                            <div className={`w-4 h-4 flex items-center justify-center flex-shrink-0 ${
                              editPerms.includes(key) ? 'bg-[#C5A059] text-white' : 'border border-gray-300'
                            }`}>
                              {editPerms.includes(key) && <Check className="w-3 h-3" />}
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-[#1f1f1f] truncate">{key}</p>
                              <p className="text-[10px] text-gray-400 truncate">{desc}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RolesPermissionsPage;
